import { ButtonBase, SvgIconTypeMap, Typography } from '@mui/material';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import { FunctionComponent, SVGProps } from 'react';
import { makeStyles } from 'tss-react/mui';
import { getDenseResponsiveFontSize } from 'theme/watchdutyTheme.utils';
import { microCapsLabelSx } from '../MapControls.constants';

type MapControlButtonSize = 'large' | 'small';

type MapControlButtonProps =
  | {
      Icon?:
        | FunctionComponent<SVGProps<SVGSVGElement>>
        | OverridableComponent<SvgIconTypeMap>;
      text: string;
      onClick: () => void;
      disabled?: boolean;
      className?: string;
      size?: MapControlButtonSize;
      iconClassName?: string;
    }
  | {
      Icon:
        | FunctionComponent<SVGProps<SVGSVGElement>>
        | OverridableComponent<SvgIconTypeMap>;
      ariaLabel: string;
      onClick: () => void;
      disabled?: boolean;
      className?: string;
      size?: MapControlButtonSize;
      iconClassName?: string;
    };

const useButtonStyles = makeStyles<{ size: MapControlButtonSize }>()((
  theme,
  { size },
) => {
  const { spacing, palette } = theme;
  // CSS variables so ForcedTheme (e.g. radar overlay) can scope dark control surfaces
  // without relying on theme.palette being re-resolved at render time (MUI v7).
  const controlBorderColor =
    palette.outline ?? palette.divider ?? theme.palette.grey[300];

  const buttonWidthHeightSpacing = size === 'large' ? 7 : 4;
  const iconWidthHeightPx = size === 'large' ? 24 : 16;

  return {
    controlButton: {
      width: `${spacing(buttonWidthHeightSpacing)} !important`,
      height: `${spacing(buttonWidthHeightSpacing)} !important`,
      display: 'flex !important',
      flexDirection: 'column',
      backgroundColor: 'var(--mui-palette-button-surface-dark)',
      boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
      borderRadius: '8px',
      margin: '16px 16px 0 16px',
      pointerEvents: 'auto',
      borderColor: controlBorderColor,
    },
    controlIcon: {
      width: iconWidthHeightPx,
      height: iconWidthHeightPx,
      color: 'var(--mui-palette-text-primary)',
    },
    text: {
      color: 'var(--mui-palette-text-primary)',
    },
    textNoIcon: {
      padding: 0,
      margin: 0,
    },
  };
});

export const useButtonGroupStyles = makeStyles()((theme) => {
  const groupDividerColor =
    theme.palette.outline ?? theme.palette.divider ?? theme.palette.grey[300];

  return {
    groupTopButton: {
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
      borderBottomWidth: 1,
      borderBottomStyle: 'solid',
      borderBottomColor: groupDividerColor,
    },
    groupBottomButton: {
      marginTop: 0,
      borderTopLeftRadius: 0,
      borderTopRightRadius: 0,
    },
  };
});

export const MapControlButton = (props: MapControlButtonProps): JSX.Element => {
  const {
    size = 'large',
    Icon,
    onClick,
    disabled,
    className = '',
    iconClassName = '',
  } = props;
  const { classes } = useButtonStyles({ size });

  const fullClassName = `${classes.controlButton} ${className}`;
  if ('text' in props) {
    const { text } = props;

    return (
      <ButtonBase
        className={fullClassName}
        onClick={onClick}
        disabled={disabled}
      >
        {Icon && <Icon className={`${classes.controlIcon} ${iconClassName}`} />}
        <Typography
          component="span"
          className={
            Icon ? classes.text : `${classes.text} ${classes.textNoIcon}`
          }
          sx={
            Icon
              ? microCapsLabelSx
              : {
                  ...microCapsLabelSx,
                  fontSize: getDenseResponsiveFontSize('1rem'),
                }
          }
        >
          {text}
        </Typography>
      </ButtonBase>
    );
  }

  const { ariaLabel } = props;

  return (
    <ButtonBase
      className={fullClassName}
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
    >
      {Icon && <Icon className={classes.controlIcon} />}
    </ButtonBase>
  );
};
